import { Suspense } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { ScrollToTop } from "@/components/ScrollToTop";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { useGoogleAnalytics } from "@/hooks/useGoogleAnalytics";
import { Layout } from "@/components/layout/Layout";
import { LocaleRoute } from "@/components/LocaleRoute";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { LEGACY_REDIRECTS, getLocalizedPath, type Locale } from "@/lib/routes";
import { getPostBySlugForLocale, getPostByAnySlug } from "@/content/blog";

// Eagerly load critical + error routes for correct prerendered HTML
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";

const About = lazyWithRetry(() => import("./pages/About"));
const Services = lazyWithRetry(() => import("./pages/Services"));
const CaseStudies = lazyWithRetry(() => import("./pages/CaseStudies"));
const Contact = lazyWithRetry(() => import("./pages/Contact"));

const Consultation = lazyWithRetry(() => import("./pages/Consultation"));
const ConsultationSuccess = lazyWithRetry(() => import("./pages/ConsultationSuccess"));
const Reschedule = lazyWithRetry(() => import("./pages/Reschedule"));
const Cancel = lazyWithRetry(() => import("./pages/Cancel"));
const Privacy = lazyWithRetry(() => import("./pages/Privacy"));
const Terms = lazyWithRetry(() => import("./pages/Terms"));
const Imprint = lazyWithRetry(() => import("./pages/Imprint"));
const WebsiteFuerPraxen = lazyWithRetry(() => import("./pages/WebsiteFuerPraxen"));
const TerminbuchungPraxis = lazyWithRetry(() => import("./pages/TerminbuchungPraxis"));
const TelefonservicePraxis = lazyWithRetry(() => import("./pages/TelefonservicePraxis"));
const Blog = lazyWithRetry(() => import("./pages/Blog"));
const BlogPost = lazyWithRetry(() => import("./pages/BlogPost"));
const WhoItIsFor = lazyWithRetry(() => import("./pages/WhoItIsFor"));

// Care package premium website preview (cinematic, dark, scoped)
const CarePreview = lazyWithRetry(() => import("./features/care-preview/CarePreview"));

// Flow package premium website preview (booking + skin-check, scoped)
const FlowPreviewPage = lazyWithRetry(() => import("./features/flow-preview/pages/FlowPreviewPage"));

// Foundation package entry-level website preview (warm rose-beige, scoped)
const FoundationPreviewPage = lazyWithRetry(() => import("./features/foundation-preview/pages/FoundationPreviewPage"));

// Foundation multi-page website preview (placeholder, scoped)
const FoundationMultiPreview = lazyWithRetry(() => import("./pages/FoundationMultiPreview"));

// CARDIO NOVA cardiology practice preview (scoped, multi-page, dark premium)
const CardioNovaIndex = lazyWithRetry(() => import("./previews/cardio-nova/pages/Index"));
const CardioNovaLeistungen = lazyWithRetry(() => import("./previews/cardio-nova/pages/LeistungenPage"));
const CardioNovaUeberUns = lazyWithRetry(() => import("./previews/cardio-nova/pages/UeberUnsPage"));
const CardioNovaAblauf = lazyWithRetry(() => import("./previews/cardio-nova/pages/AblaufPage"));
const CardioNovaPreise = lazyWithRetry(() => import("./previews/cardio-nova/pages/PreisePage"));
const CardioNovaKontakt = lazyWithRetry(() => import("./previews/cardio-nova/pages/KontaktPage"));
const CardioNovaImpressum = lazyWithRetry(() => import("./previews/cardio-nova/pages/ImpressumPage"));
const CardioNovaDatenschutz = lazyWithRetry(() => import("./previews/cardio-nova/pages/DatenschutzPage"));

// Admin wrapper - lazy loaded with AuthProvider inside
const AdminWrapper = lazyWithRetry(() => import("./components/admin/AdminWrapper"));
import { AdminNoIndex } from "./components/admin/AdminNoIndex";

// Lazy load toast components - only needed after user interaction
const Toaster = lazyWithRetry(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazyWithRetry(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));

// Component to initialize analytics inside Router context
function AnalyticsProvider() {
  useGoogleAnalytics();
  return null;
}

const queryClient = new QueryClient();

// /blog/:slug → /de/blog/:slug (preserve slug)
function LegacyBlogPostRedirect() {
  const { slug } = useParams();
  return <Navigate to={slug ? `/de/blog/${slug}` : "/de/blog"} replace />;
}

// Resolve a blog post slug at the route level so BlogPost only ever
// mounts when the URL is canonical and the post exists. This avoids
// in-component <Navigate> redirects that cause double-mounts and
// flicker on language switch.
function BlogPostRoute({ locale }: { locale: Locale }) {
  const { slug } = useParams<{ slug: string }>();
  const blogIndex = getLocalizedPath("blog", locale);

  if (!slug) return <Navigate to={blogIndex} replace />;

  const post = getPostBySlugForLocale(slug, locale);
  if (post) {
    // EN visitors should never see DE-only posts.
    if (locale === "en" && !post.en) {
      return <Navigate to={blogIndex} replace />;
    }
    return <BlogPost />;
  }

  // Slug not canonical for this locale. Render the post directly using the
  // canonical slug for this locale so:
  //   1. Prerendered HTML (LovableHTML) contains the full article body —
  //      a client-side <Navigate> would only emit an empty shell to bots.
  //   2. SEOHead emits <link rel="canonical"> pointing at the canonical
  //      URL, so search engines consolidate to the correct URL.
  //   3. BlogPost performs a history.replaceState() to swap the URL bar
  //      to the canonical path for human visitors — no flash, no remount.
  const fallback = getPostByAnySlug(slug);
  if (fallback) {
    if (locale === "en" && !fallback.post.slug.en) {
      return <Navigate to={blogIndex} replace />;
    }
    const canonicalSlug =
      locale === "en"
        ? fallback.post.slug.en ?? fallback.post.slug.de
        : fallback.post.slug.de;
    return <BlogPost overrideSlug={canonicalSlug} />;
  }

  return <Navigate to={blogIndex} replace />;
}


const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <LanguageProvider>
          <TooltipProvider>
            <Suspense fallback={null}>
              <Toaster />
              <Sonner />
            </Suspense>
            <ScrollToTop />
            <AnalyticsProvider />
            <Routes>
              {/* Routes wrapped in persistent Layout */}
              <Route element={<Layout />}>
                {/* German routes */}
                <Route path="/de" element={<LocaleRoute locale="de"><Index /></LocaleRoute>} />
                <Route path="/de/leistungen" element={<LocaleRoute locale="de"><Services /></LocaleRoute>} />
                <Route path="/de/ueber-uns" element={<LocaleRoute locale="de"><About /></LocaleRoute>} />
                <Route path="/de/praxisbeispiele" element={<LocaleRoute locale="de"><CaseStudies /></LocaleRoute>} />
                <Route path="/de/kontakt" element={<LocaleRoute locale="de"><Contact /></LocaleRoute>} />
                <Route path="/de/erstgespraech" element={<LocaleRoute locale="de"><Consultation /></LocaleRoute>} />
                <Route path="/de/website-fuer-praxen" element={<LocaleRoute locale="de"><WebsiteFuerPraxen /></LocaleRoute>} />
                <Route path="/de/terminbuchung-praxis" element={<LocaleRoute locale="de"><TerminbuchungPraxis /></LocaleRoute>} />
                <Route path="/de/telefonservice-praxis" element={<LocaleRoute locale="de"><TelefonservicePraxis /></LocaleRoute>} />
                <Route path="/de/datenschutz" element={<LocaleRoute locale="de"><Privacy /></LocaleRoute>} />
                <Route path="/de/impressum" element={<LocaleRoute locale="de"><Imprint /></LocaleRoute>} />
                <Route path="/de/agb" element={<LocaleRoute locale="de"><Terms /></LocaleRoute>} />
                <Route path="/de/blog" element={<LocaleRoute locale="de"><Blog /></LocaleRoute>} />
                <Route path="/de/blog/:slug" element={<LocaleRoute locale="de"><BlogPostRoute locale="de" /></LocaleRoute>} />
                <Route path="/de/fuer-wen" element={<LocaleRoute locale="de"><WhoItIsFor /></LocaleRoute>} />

                {/* English routes */}
                <Route path="/en" element={<LocaleRoute locale="en"><Index /></LocaleRoute>} />
                <Route path="/en/services" element={<LocaleRoute locale="en"><Services /></LocaleRoute>} />
                <Route path="/en/about" element={<LocaleRoute locale="en"><About /></LocaleRoute>} />
                <Route path="/en/case-studies" element={<LocaleRoute locale="en"><CaseStudies /></LocaleRoute>} />
                <Route path="/en/contact" element={<LocaleRoute locale="en"><Contact /></LocaleRoute>} />
                <Route path="/en/consultation" element={<LocaleRoute locale="en"><Consultation /></LocaleRoute>} />
                <Route path="/en/medical-website" element={<LocaleRoute locale="en"><WebsiteFuerPraxen /></LocaleRoute>} />
                <Route path="/en/appointment-booking" element={<LocaleRoute locale="en"><TerminbuchungPraxis /></LocaleRoute>} />
                <Route path="/en/phone-service" element={<LocaleRoute locale="en"><TelefonservicePraxis /></LocaleRoute>} />
                <Route path="/en/privacy" element={<LocaleRoute locale="en"><Privacy /></LocaleRoute>} />
                <Route path="/en/imprint" element={<LocaleRoute locale="en"><Imprint /></LocaleRoute>} />
                <Route path="/en/terms" element={<LocaleRoute locale="en"><Terms /></LocaleRoute>} />
                <Route path="/en/blog" element={<LocaleRoute locale="en"><Blog /></LocaleRoute>} />
                <Route path="/en/blog/:slug" element={<LocaleRoute locale="en"><BlogPostRoute locale="en" /></LocaleRoute>} />
                <Route path="/en/who-it-is-for" element={<LocaleRoute locale="en"><WhoItIsFor /></LocaleRoute>} />

                {/* Utility / post-conversion routes (not part of /de|/en tree) */}
                <Route path="/consultation-success" element={<ConsultationSuccess />} />
                <Route path="/reschedule" element={<Reschedule />} />
                <Route path="/cancel" element={<Cancel />} />

                {/* Legacy blog post slug → /de/blog/:slug */}
                <Route path="/blog/:slug" element={<LegacyBlogPostRedirect />} />

                {/* Legacy → /de|/en redirects (SPA fallback; true 301s handled at hosting layer if configured) */}
                {LEGACY_REDIRECTS.map((r) => (
                  <Route key={r.from} path={r.from} element={<Navigate to={r.to} replace />} />
                ))}
              </Route>


              {/* Admin Routes - AuthProvider loaded only when accessing /admin/*.
                  AdminNoIndex emits <meta name="robots" content="noindex, nofollow">
                  synchronously so the tag is baked into prerendered HTML, not
                  hidden behind the lazy AdminWrapper Suspense boundary. */}
              <Route
                path="/admin/*"
                element={
                  <>
                    <AdminNoIndex />
                    <Suspense fallback={null}><AdminWrapper /></Suspense>
                  </>
                }
              />

              {/* Care package premium website preview — standalone (no shared Layout), locale-prefixed */}
              <Route
                path="/de/preview/care/*"
                element={
                  <LocaleRoute locale="de">
                    <Suspense fallback={null}><CarePreview /></Suspense>
                  </LocaleRoute>
                }
              />
              <Route
                path="/en/preview/care/*"
                element={
                  <LocaleRoute locale="en">
                    <Suspense fallback={null}><CarePreview /></Suspense>
                  </LocaleRoute>
                }
              />
              {/* Legacy unprefixed preview URL → DE preview */}
              <Route path="/preview/care" element={<Navigate to="/de/preview/care" replace />} />

              {/* Flow package premium website preview — standalone multi-page, locale-prefixed */}
              <Route
                path="/de/preview/flow/*"
                element={
                  <LocaleRoute locale="de">
                    <Suspense fallback={null}><FlowPreviewPage /></Suspense>
                  </LocaleRoute>
                }
              />
              <Route
                path="/en/preview/flow/*"
                element={
                  <LocaleRoute locale="en">
                    <Suspense fallback={null}><FlowPreviewPage /></Suspense>
                  </LocaleRoute>
                }
              />
              {/* Legacy unprefixed preview URL → DE preview */}
              <Route path="/preview/flow" element={<Navigate to="/de/preview/flow" replace />} />

              {/* Start package entry-level website preview — standalone (no shared Layout), locale-prefixed */}
              <Route
                path="/de/preview/start"
                element={
                  <LocaleRoute locale="de">
                    <Suspense fallback={null}><FoundationPreviewPage /></Suspense>
                  </LocaleRoute>
                }
              />
              <Route
                path="/en/preview/start"
                element={
                  <LocaleRoute locale="en">
                    <Suspense fallback={null}><FoundationPreviewPage /></Suspense>
                  </LocaleRoute>
                }
              />
              {/* Legacy preview URLs */}
              <Route path="/preview/foundation" element={<Navigate to="/de/preview/foundation" replace />} />
              <Route path="/preview/start" element={<Navigate to="/de/preview/start" replace />} />

              {/* Foundation multi-page website preview — standalone, locale-prefixed */}
              <Route
                path="/de/preview/foundation-multi"
                element={
                  <LocaleRoute locale="de">
                    <Suspense fallback={null}><FoundationMultiPreview /></Suspense>
                  </LocaleRoute>
                }
              />
              <Route
                path="/en/preview/foundation-multi"
                element={
                  <LocaleRoute locale="en">
                    <Suspense fallback={null}><FoundationMultiPreview /></Suspense>
                  </LocaleRoute>
                }
              />

              {/* CARDIO NOVA — Foundation preview, locale-prefixed (DE + EN) */}
              {(['de', 'en'] as const).map((loc) => (
                <Route key={loc}>
                  <Route path={`/${loc}/preview/foundation`} element={<LocaleRoute locale={loc}><Suspense fallback={null}><CardioNovaIndex /></Suspense></LocaleRoute>} />
                  <Route path={`/${loc}/preview/foundation/leistungen`} element={<LocaleRoute locale={loc}><Suspense fallback={null}><CardioNovaLeistungen /></Suspense></LocaleRoute>} />
                  <Route path={`/${loc}/preview/foundation/ueber-uns`} element={<LocaleRoute locale={loc}><Suspense fallback={null}><CardioNovaUeberUns /></Suspense></LocaleRoute>} />
                  <Route path={`/${loc}/preview/foundation/ablauf`} element={<LocaleRoute locale={loc}><Suspense fallback={null}><CardioNovaAblauf /></Suspense></LocaleRoute>} />
                  <Route path={`/${loc}/preview/foundation/preise`} element={<LocaleRoute locale={loc}><Suspense fallback={null}><CardioNovaPreise /></Suspense></LocaleRoute>} />
                  <Route path={`/${loc}/preview/foundation/kontakt`} element={<LocaleRoute locale={loc}><Suspense fallback={null}><CardioNovaKontakt /></Suspense></LocaleRoute>} />
                  <Route path={`/${loc}/preview/foundation/impressum`} element={<LocaleRoute locale={loc}><Suspense fallback={null}><CardioNovaImpressum /></Suspense></LocaleRoute>} />
                  <Route path={`/${loc}/preview/foundation/datenschutz`} element={<LocaleRoute locale={loc}><Suspense fallback={null}><CardioNovaDatenschutz /></Suspense></LocaleRoute>} />
                </Route>
              ))}

              {/* Legacy unprefixed cardio-nova URLs → new DE Foundation preview */}
              <Route path="/previews/cardio-nova" element={<Navigate to="/de/preview/foundation" replace />} />
              <Route path="/previews/cardio-nova/*" element={<Navigate to="/de/preview/foundation" replace />} />

              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </TooltipProvider>
        </LanguageProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
};

export default App;
